import { useEffect, useState } from "react";
import { handleTokenAndPostRequest } from "../utils/utils";

// Shared signal for whether the signed-in agent has any rows in
// production.contacts_merged. Demo views (sample contacts on /contacts,
// sample drafts on /needs-review, sample threads on /ai-inbox) must only
// render when this is definitively `false` — an empty list alone is not
// enough (the user may simply have filtered everything out, or a fetch may
// have errored).
//
// Returns one of:
//   undefined → still loading / unknown — callers MUST NOT show demo data yet
//   true      → agent has contacts → never show demo
//   false     → agent has no contacts → demo is allowed
//
// The result is cached at module scope so every page that mounts only
// triggers a single backend call per session.

let cached;
let inflight;
const subscribers = new Set();

function notify(value) {
  cached = value;
  subscribers.forEach((fn) => fn(value));
}

function fetchHasContacts() {
  if (inflight) return inflight;
  inflight = handleTokenAndPostRequest(`${window.api_host}/authenticate-token-agent`)
    .then((res) => {
      // Treat anything other than an explicit `false` as "has contacts" so a
      // malformed/blocked response (e.g. demo guard) never accidentally
      // unlocks the demo view for a real agent.
      const value = res?.data?.has_contacts === false;
      notify(value);
      return value;
    })
    .catch(() => {
      // On error, assume the agent has contacts so we never accidentally
      // overlay demo data on top of (or in place of) real data.
      notify(false);
      return false;
    })
    .finally(() => { inflight = null; });
  return inflight;
}

export default function useAgentHasNoContacts() {
  const [value, setValue] = useState(cached);
  useEffect(() => {
    subscribers.add(setValue);
    if (cached === undefined) fetchHasContacts();
    return () => { subscribers.delete(setValue); };
  }, []);
  return value;
}
